import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Heading, Pressable, Skeleton, Stack } from '@fluent/ui';
import { AlbumLook } from './AlbumLook.js';
import type { CefrCode, PhraseLessonView, PhrasesView as PhrasesViewData } from './progression.js';
import './Phrases.css';

/**
 * The ფრაზები screen body — presentational (view in, taps out), shared by
 * the real /phrases screen and the no-sign-in /phrases-review design route.
 *
 * Sticky top bar: title + phrases learned, and one chip per level A1…C2
 * (tap = jump to that level; the chip of the level on screen is filled, a
 * finished level's chip is outlined in its colour). Below it the lessons
 * as a sticker album (Tornike's pick, 2026-10-18 — AlbumLook).
 */

export interface PhrasesViewProps {
  view: PhrasesViewData;
  onOpen: (lesson: PhraseLessonView) => void;
  /** First load — completions unknown: skeleton instead of wrong states. */
  pending?: boolean;
  /** Optional line under the top bar (the design route's "sample data"). */
  notice?: ReactNode;
}

/** Bottom tab bar clearance used by the auto-scroll (matches Home). */
const TAB_BAR_CLEARANCE = 96;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

export function PhrasesView({ view, onOpen, pending = false, notice }: PhrasesViewProps) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState<CefrCode | null>(view.levels[0]?.code ?? null);

  const headerBottom = useCallback(() => headerRef.current?.getBoundingClientRect().bottom ?? 0, []);

  // Scroll-spy: the chip of the level whose section is under the top bar.
  useEffect(() => {
    if (pending) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const sections = rootRef.current?.querySelectorAll<HTMLElement>('[data-phrases-level]');
      if (!sections?.length) return;
      const line = headerBottom() + 24;
      let current = sections[0]?.dataset['phrasesLevel'] ?? null;
      for (const section of sections) {
        if (section.getBoundingClientRect().top <= line) current = section.dataset['phrasesLevel'] ?? current;
      }
      // At the very bottom the last level may never reach the line: it is
      // the one on screen (and the one its chip just jumped to).
      const atBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2 && window.scrollY > 0;
      if (atBottom) current = sections[sections.length - 1]?.dataset['phrasesLevel'] ?? current;
      setActive(current as CefrCode | null);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [pending, headerBottom]);

  // Bring the next lesson on screen once, when the real states arrive — but
  // only if it is off screen (Home's F044 rule: never yank a page that
  // already shows what the learner needs).
  const scrolledRef = useRef(false);
  useEffect(() => {
    if (pending || scrolledRef.current) return;
    scrolledRef.current = true;
    const el = rootRef.current?.querySelector<HTMLElement>('[data-phrases-next]');
    if (!el) return;
    const r = el.getBoundingClientRect();
    const top = headerBottom();
    const offScreen = r.bottom < top || r.top > window.innerHeight - TAB_BAR_CLEARANCE;
    if (!offScreen) return;
    const visibleMiddle = top + (window.innerHeight - TAB_BAR_CLEARANCE - top) / 2;
    window.scrollTo({
      top: Math.max(0, window.scrollY + r.top + r.height / 2 - visibleMiddle),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }, [pending, headerBottom]);

  const jumpTo = (code: CefrCode) => {
    const section = rootRef.current?.querySelector<HTMLElement>(`[data-phrases-level="${code}"]`);
    if (!section) return;
    setActive(code);
    const gap = 8;
    window.scrollTo({
      top: Math.max(0, window.scrollY + section.getBoundingClientRect().top - headerBottom() - gap),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  return (
    <div className="fluent-phrases" ref={rootRef}>
      <header className="fluent-phrases__topbar" ref={headerRef}>
        <Container size="sm">
          <div className="fluent-phrases__topbar-inner">
            <div className="fluent-phrases__title-row">
              <Heading level={3} as="h1" className="fluent-phrases__title">
                {t('phrases.title')}
              </Heading>
              <span
                className="fluent-phrases__count"
                aria-label={t('phrases.count_aria', { learned: view.phrasesLearned, total: view.phrasesTotal })}
              >
                {t('phrases.count', { learned: view.phrasesLearned, total: view.phrasesTotal })}
              </span>
            </div>
            <div className="fluent-phrases__chips" role="group" aria-label={t('phrases.levels_aria')}>
              {view.levels.map((level) => {
                const finished = level.total > 0 && level.done === level.total;
                return (
                  <Pressable
                    key={level.unitId}
                    className="fluent-phrases__chip"
                    data-cefr={level.code.toLowerCase()}
                    data-active={active === level.code || undefined}
                    data-done={finished || undefined}
                    aria-label={t(finished ? 'phrases.chip_done_aria' : 'phrases.chip_aria', {
                      code: level.code,
                      title: level.title,
                    })}
                    aria-current={active === level.code ? 'true' : undefined}
                    onClick={() => jumpTo(level.code)}
                    data-testid={`phrases-chip-${level.code}`}
                  >
                    {level.code}
                  </Pressable>
                );
              })}
            </div>
          </div>
        </Container>
      </header>
      <Container size="sm">
        <div className="fluent-phrases__body" aria-busy={pending || undefined}>
          {notice}
          {pending ? <PhrasesSkeleton /> : <AlbumLook levels={view.levels} onOpen={onOpen} />}
        </div>
      </Container>
    </div>
  );
}

/**
 * While the phrases lessons download (they are not in the app's start-up
 * bundle — content/lessons.ts): the album's frame with stand-ins. The title
 * is real; the count, the level chips and the stickers shimmer.
 */
export function PhrasesLoading({ levelCount }: { levelCount: number }) {
  const { t } = useTranslation();
  return (
    <div className="fluent-phrases" data-testid="phrases-loading">
      <header className="fluent-phrases__topbar">
        <Container size="sm">
          <div className="fluent-phrases__topbar-inner">
            <div className="fluent-phrases__title-row">
              <Heading level={3} as="h1" className="fluent-phrases__title">
                {t('phrases.title')}
              </Heading>
              <Skeleton shape="pill" width={88} height={12} />
            </div>
            <div className="fluent-phrases__chips" aria-hidden="true">
              {Array.from({ length: levelCount }, (_, i) => (
                <Skeleton key={i} shape="block" className="fluent-phrases__chip-skeleton" />
              ))}
            </div>
          </div>
        </Container>
      </header>
      <Container size="sm">
        <div className="fluent-phrases__body" aria-busy="true">
          <PhrasesSkeleton />
        </div>
      </Container>
    </div>
  );
}

/** First-load placeholder in the album's shape: a level label and two rows of
 *  round sticker slots, so nothing jumps when the real page lands. */
function PhrasesSkeleton() {
  return (
    <Stack gap={4} data-testid="phrases-skeleton">
      <Skeleton shape="pill" width={140} height={24} />
      <div className="fluent-phrases__grid">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Stack key={i} gap={2} style={{ alignItems: 'center' }}>
            <Skeleton shape="circle" width={84} height={84} />
            <Skeleton shape="line" width="70%" height={12} />
          </Stack>
        ))}
      </div>
    </Stack>
  );
}
